import { Btn, type InputSample, clamp, wrapAngle } from "@rivalwatch/shared";

const KEYMAP: Record<string, number> = {
  Space: Btn.Jump,
  ControlLeft: Btn.Crouch,
  KeyC: Btn.Crouch,
  ShiftLeft: Btn.Ability1,
  KeyE: Btn.Ability2,
  KeyQ: Btn.Ultimate,
  KeyR: Btn.Reload,
  KeyV: Btn.Melee,
  KeyF: Btn.Ability3,
  Digit1: Btn.Weapon1,
  Digit2: Btn.Weapon2,
};

export interface InputSettings {
  sensitivity: number; // radians per pixel
  invertY: boolean;
  stickSensitivity: number; // radians per second at full deflection
}

/** Keyboard + mouse (Pointer Lock) + gamepad → InputSample for the sim. */
export class Input {
  yaw = 0;
  pitch = 0;
  private keys = new Set<string>();
  private mouse = 0;
  enabled = true;
  /** Look-sensitivity multiplier (e.g. scoped). */
  sensMul = 1;
  locked = false;
  settings: InputSettings = { sensitivity: 0.0022, invertY: false, stickSensitivity: 3.2 };
  private pad?: Gamepad;
  private disposers: (() => void)[] = [];
  /**
   * Mouse-wheel notches become WeaponNext presses. Frames can run no sim tick at all, so a notch
   * stays pressed until a tick has seen it (`ticked`), then releases for a tick so the next notch
   * is a fresh press.
   */
  private wheelPending = 0;
  private wheelState: "idle" | "pressed" | "releasing" = "idle";

  constructor(private readonly el: HTMLElement) {
    const on = <K extends keyof WindowEventMap>(t: K, f: (e: WindowEventMap[K]) => void, target: EventTarget = window) => {
      target.addEventListener(t, f as EventListener);
      this.disposers.push(() => target.removeEventListener(t, f as EventListener));
    };
    on("keydown", (e) => {
      if (!this.enabled || isTyping(e)) return;
      this.keys.add(e.code);
      if (e.code in KEYMAP || e.code === "Tab") e.preventDefault();
    });
    on("keyup", (e) => this.keys.delete(e.code));
    on("blur", () => {
      this.keys.clear();
      this.mouse = 0;
      this.wheelPending = 0;
    });
    // Pointer events, not mouse events: if anything calls preventDefault() on pointerdown,
    // browsers suppress the compatibility mousedown/mousemove until the button is released.
    // Button state changes only on real transitions. Under pointer lock Chrome can report
    // `buttons: 0` on plain pointermoves while a button is still held, so movement must never
    // touch it — otherwise aiming while firing silently lets go of the trigger.
    const BTN = [Btn.Primary, Btn.Melee, Btn.Secondary]; // by MouseEvent.button: left, middle, right
    const DOM_BIT = [1, 4, 2]; // the same buttons in the MouseEvent.buttons bitmask
    const press = (button: number) => {
      if (this.locked) this.mouse |= BTN[button] ?? 0;
    };
    const release = (button: number) => {
      this.mouse &= ~(BTN[button] ?? 0);
    };
    const mouseOnly = (e: PointerEvent) => e.pointerType === "mouse";
    on("pointerdown", (e) => mouseOnly(e) && press(e.button));
    on("pointerup", (e) => mouseOnly(e) && release(e.button));
    on("pointercancel", (e) => {
      if (mouseOnly(e)) this.mouse = 0;
    });
    // Compatibility events as a second, idempotent source (when nothing suppresses them).
    on("mousedown", (e) => press(e.button));
    on("mouseup", (e) => release(e.button));
    on("contextmenu", (e) => e.preventDefault(), el);
    on("wheel", (e) => {
      if (this.locked && this.enabled && e.deltaY !== 0) this.wheelPending = Math.min(4, this.wheelPending + 1);
    });
    on("pointermove", (e) => {
      if (!mouseOnly(e)) return;
      // A second button pressed or released while another is held arrives as a pointermove
      // with `button` set to the button that changed (chorded buttons). Under pointer lock
      // Chrome gives those a bogus movement delta (the view jumped when you fired while
      // scoped), so they only ever change buttons, never the look.
      if (e.button >= 0) {
        if (e.buttons & (DOM_BIT[e.button] ?? 0)) press(e.button);
        else release(e.button);
        return;
      }
      if (!this.locked || !this.enabled) return;
      const k = this.settings.sensitivity * this.sensMul;
      this.yaw = wrapAngle(this.yaw - e.movementX * k);
      this.pitch = clamp(this.pitch - e.movementY * k * (this.settings.invertY ? -1 : 1), -1.45, 1.45);
    });
    const onLock = () => {
      this.locked = document.pointerLockElement === el;
      if (!this.locked) this.mouse = 0;
    };
    document.addEventListener("pointerlockchange", onLock);
    this.disposers.push(() => document.removeEventListener("pointerlockchange", onLock));
  }

  lock() {
    if (!this.locked) this.el.requestPointerLock?.()?.catch?.(() => {});
  }

  unlock() {
    if (this.locked) document.exitPointerLock();
  }

  /** Call after a sample has driven at least one sim tick. */
  ticked() {
    if (this.wheelState === "pressed") this.wheelState = "releasing";
    else if (this.wheelState === "releasing") this.wheelState = "idle";
  }

  /** Samples current input; `dt` drives gamepad look. */
  sample(dt: number): InputSample {
    let buttons = this.mouse;
    if (this.wheelState === "idle" && this.wheelPending > 0) {
      this.wheelPending--;
      this.wheelState = "pressed";
    }
    if (this.wheelState === "pressed") buttons |= Btn.WeaponNext;
    let moveX = 0;
    let moveZ = 0;
    if (this.enabled) {
      for (const k of this.keys) buttons |= KEYMAP[k] ?? 0;
      if (this.keys.has("KeyW")) moveZ += 1;
      if (this.keys.has("KeyS")) moveZ -= 1;
      if (this.keys.has("KeyD")) moveX += 1;
      if (this.keys.has("KeyA")) moveX -= 1;
    }
    const pad = this.readPad();
    if (pad && this.enabled) {
      const dz = (v: number) => (Math.abs(v) < 0.15 ? 0 : (v - Math.sign(v) * 0.15) / 0.85);
      moveX += dz(pad.axes[0] ?? 0);
      moveZ -= dz(pad.axes[1] ?? 0);
      const lx = dz(pad.axes[2] ?? 0);
      const ly = dz(pad.axes[3] ?? 0);
      const curve = (v: number) => Math.sign(v) * v * v;
      this.yaw = wrapAngle(this.yaw - curve(lx) * this.settings.stickSensitivity * dt);
      this.pitch = clamp(this.pitch - curve(ly) * this.settings.stickSensitivity * 0.7 * dt * (this.settings.invertY ? -1 : 1), -1.45, 1.45);
      const b = (i: number) => !!pad.buttons[i]?.pressed;
      if (b(7)) buttons |= Btn.Primary;
      if (b(6)) buttons |= Btn.Secondary;
      if (b(0)) buttons |= Btn.Jump;
      if (b(1)) buttons |= Btn.Crouch;
      if (b(5)) buttons |= Btn.Ability1;
      if (b(4)) buttons |= Btn.Ability2;
      if (b(10)) buttons |= Btn.Ability3;
      if (b(3)) buttons |= Btn.Ultimate;
      if (b(2)) buttons |= Btn.Reload;
      if (b(11)) buttons |= Btn.Melee;
      if (b(12)) buttons |= Btn.WeaponNext;
    }
    const l = Math.hypot(moveX, moveZ);
    if (l > 1) {
      moveX /= l;
      moveZ /= l;
    }
    return { buttons, moveX, moveZ, yaw: this.yaw, pitch: this.pitch };
  }

  isDown(code: string) {
    return this.keys.has(code);
  }

  get gamepadConnected() {
    return !!this.pad;
  }

  private readPad(): Gamepad | undefined {
    const pads = navigator.getGamepads?.() ?? [];
    this.pad = [...pads].find((p): p is Gamepad => !!p && p.connected);
    return this.pad;
  }

  dispose() {
    for (const d of this.disposers) d();
    this.unlock();
  }
}

function isTyping(e: KeyboardEvent) {
  const t = e.target as HTMLElement | null;
  return !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
}
